import React, { useCallback, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Empty } from 'antd';
import { AppButton, AppModal, AppSpin, AppTable } from '@/components/atoms';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useLongTermPatients } from '@/hooks/main/useRezeptInit';
import type { LongTermPatientItem } from '@/services/main/rezept/rezeptListService';
import { convertWarekiDetail, convertWarekiMonth } from '@/utils/wareki';
import { formatTreatementYearmonth } from '../utils';
import useAppModal from '@/hooks/useAppModal';

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8000') + '/api/v1';

/** Fetch a protected PDF endpoint using the stored auth token and open in new tab. */
async function openProtectedPdf(apiPath: string): Promise<void> {
  const token = localStorage.getItem('auth_token');
  const serviceType = localStorage.getItem('service_type');
  const fullUrl = apiPath.startsWith('http') ? apiPath : `${API_BASE_URL}${apiPath}`;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (serviceType) headers['X-Service-Type'] = serviceType;
  const resp = await fetch(fullUrl, { headers });
  if (!resp.ok) throw new Error(`PDF request failed: ${resp.status}`);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  // Revoke after tab loads to free memory
  if (win) win.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
  else URL.revokeObjectURL(url);
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  searchYearmonth: string;
  massagerId?: string;
}

const LongTermPatientsModal: React.FC<Props> = ({ isOpen, onClose, searchYearmonth, massagerId }) => {
  const { siteType } = useTheme();
  const { clinicNumber } = useClinicContext();
  const appModal = useAppModal();
  const { fetchLongTermPatients, isLoading } = useLongTermPatients();
  const [patients, setPatients] = useState<LongTermPatientItem[]>([]);
  const [fetched, setFetched] = useState(false);

  const handlePrintList = useCallback(() => {
    if (patients.length === 0) {
      appModal.error({ content: '月16回受診者が存在しません。' });
      return;
    }
    // Re-query via BE PDF endpoint with auth token (server re-queries DB at print time per BD spec)
    const params = new URLSearchParams({ search_yearmonth: searchYearmonth });
    if (massagerId) params.append('massager_id', massagerId);
    const pdfUrl = `/clinic/${siteType}/${clinicNumber}/rezepts/long-term-patients-pdf?${params}`;
    openProtectedPdf(pdfUrl).catch(() => {
      appModal.error({ content: '印刷の準備に失敗しました。' });
    });
  }, [patients.length, searchYearmonth, massagerId, siteType, clinicNumber, appModal]);

  const load = useCallback(async () => {
    if (!isOpen || fetched) return;
    const data = await fetchLongTermPatients({ yearmonth: searchYearmonth, massagerId });
    setPatients(data);
    setFetched(true);
  }, [isOpen, fetched, fetchLongTermPatients, searchYearmonth, massagerId]);

  React.useEffect(() => {
    if (isOpen && !fetched) load();
    if (!isOpen) setFetched(false);
  }, [isOpen, fetched, load]);

  const formatFirstTreatmentDate = (date: string | null): string => {
    if (!date) return '';
    const [y, m, d] = date.split('-').map(Number);
    if (!y || !m || !d) return '';
    const era = convertWarekiDetail(y, m, d);
    if (!era) return '';
    return `${era}年${String(m).padStart(2, '0')}月${String(d).padStart(2, '0')}日`;
  };

  const columns: ColumnsType<LongTermPatientItem> = [
    { title: '受診者番号', dataIndex: 'serial_number', key: 'serial_number', width: 110 },
    { title: '受診者名', dataIndex: 'patient_name', key: 'patient_name', width: 160 },
    {
      title: '初療年月日',
      dataIndex: 'first_treatment_date',
      key: 'first_treatment_date',
      width: 130,
      render: v => formatFirstTreatmentDate(v),
    },
    {
      title: '施術年月',
      dataIndex: 'treatement_yearmonth',
      key: 'treatement_yearmonth',
      width: 120,
      render: v => formatTreatementYearmonth(String(v)),
    },
    { title: '当月施術回数', dataIndex: 'visit_count', key: 'visit_count', width: 110, render: v => `${v}回` },
    {
      title: '操作',
      key: 'actions',
      width: 130,
      render: (_, row) => {
        const editUrl = row.longterm_id
          ? `/${siteType}/long_term_frequents/${row.patient_id}/edit/${row.longterm_id}`
          : `/${siteType}/long_term_frequents/${row.patient_id}/edit?treatment_yearmonth=${row.treatement_yearmonth}`;
        const pdfApiPath = `/clinic/${siteType}/${clinicNumber}/patients/${row.patient_id}/long-term-frequents/${row.longterm_id}/pdf`;
        return (
          <div className="flex gap-1">
            <AppButton size="small" onClick={() => window.open(editUrl, '_blank')}>編集</AppButton>
            {row.longterm_id && (
              <AppButton size="small" onClick={() => openProtectedPdf(pdfApiPath).catch(() => {})}>出力</AppButton>
            )}
          </div>
        );
      },
    },
  ];

  const titleYm = searchYearmonth
    ? (() => { const y = Number(searchYearmonth.slice(0, 4)); const m = Number(searchYearmonth.slice(4)); return convertWarekiMonth(y, m); })()
    : '';

  return (
    <AppModal
      isOpen={isOpen}
      title={`月16回受診者一覧 ${titleYm ? `${titleYm}提出分` : ''}`}
      onClose={onClose}
      maxWidth="max-w-5xl"
      hideCancelButton
      okText="閉じる"
      onOk={onClose}
    >
      {isLoading ? (
        <div className="flex justify-center py-10"><AppSpin /></div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-600">
              {patients.length > 0 ? `${patients.length}件` : ''}
            </p>
            <AppButton onClick={handlePrintList}>一覧印刷</AppButton>
          </div>
          <AppTable<LongTermPatientItem>
            rowKey="rezept_id"
            columns={columns}
            dataSource={patients}
            pagination={false}
            scroll={{ x: 800, y: 480 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="表示するデータがありません"
                />
              ),
            }}
          />
        </>
      )}
    </AppModal>
  );
};

export default LongTermPatientsModal;
